import {
  TabsTrigger,
  TabsList,
  Tabs,
  TabsContent,
  Switch,
  Separator,
  SelectItem,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  AsyncButton,
} from '@bpinternal/ui-kit-next'
import { getUseConfigStore } from '../../stores/useConfigStore'
import { useLocalStore } from '~/stores/localStore'
import { Section } from './Section'
import { SubSection } from './SubSection'
import { ThumbsDown, ThumbsUp } from 'lucide-react'
import { cn } from '~/utils/cn'
import { Composer, Message } from '@botpress/webchat'
import WebchatComponentPreview from '../elements/WebchatComponentPreview'
import { useState, useMemo } from 'react'
import { FileCardDisplay } from '../elements/FileCardDisplay'
import { SoundSettingPreview } from '../elements/SoundSettingPreview'
import { LatestWebchatTheme } from '@bpinternal/shared'
import { AdvancedSettings } from './AdvancedSettings'
import useNavigationBlocker, { navigationBlockerOptions } from '../../hooks/useNavigationBlocker'

type Props = {
  workspaceId: string
  botId: string
}

export function FeaturesConfigSection({ workspaceId, botId }: Props) {
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null)

  const useConfigStore = getUseConfigStore({ botId, workspaceId })
  const feedbackEnabled = useConfigStore((state) => state.feedbackEnabled)
  const allowFileUpload = useConfigStore((state) => state.allowFileUpload)
  const composerPlaceholder = useConfigStore((state) => state.composerPlaceholder)
  const soundEnabled = useConfigStore((state) => state.soundEnabled)
  const storageLocation = useConfigStore((state) => state.storageLocation)
  const color = useConfigStore((state) => state.color)
  const variant = useConfigStore((state) => state.variant)
  const radius = useConfigStore((state) => state.radius)
  const headerVariant = useConfigStore((state) => state.headerVariant)
  const adminSecret = useConfigStore((state) => state.adminSecret)
  const allowedOrigins = useConfigStore((state) => state.allowedOrigins)

  const setFeedbackEnabled = useConfigStore((state) => state.setFeedbackEnabled)
  const setAllowFileUpload = useConfigStore((state) => state.setAllowFileUpload)
  const setSoundEnabled = useConfigStore((state) => state.setSoundEnabled)
  const setStorageLocation = useConfigStore((state) => state.setStorageLocation)
  const setAdminSecret = useConfigStore((state) => state.setAdminSecret)
  const setAllowedOrigins = useConfigStore((state) => state.setAllowedOrigins)

  const saveConfig = useConfigStore((state) => state.saveConfig)
  const _updating = useConfigStore((state) => state._updating)
  const _originalConfig = useConfigStore((state) => state._originalConfig)

  const dashboardTheme = useLocalStore((state) => state.getPreferredTheme())

  const webchatTheme: LatestWebchatTheme = useMemo(
    () => ({
      themeMode: dashboardTheme,
      headerVariant,
      variant,
      color,
      radius,
    }),
    [dashboardTheme, headerVariant, variant, color, radius]
  )

  const hasPendingChanges =
    feedbackEnabled !== _originalConfig?.feedbackEnabled ||
    allowFileUpload !== _originalConfig?.allowFileUpload ||
    soundEnabled !== _originalConfig?.soundEnabled ||
    storageLocation !== _originalConfig?.storageLocation ||
    adminSecret !== _originalConfig?.adminSecret ||
    JSON.stringify(allowedOrigins ?? []) !== JSON.stringify(_originalConfig?.allowedOrigins ?? [])

  useNavigationBlocker(hasPendingChanges, {
    ...navigationBlockerOptions,
    confirmCallback: async () => {
      try {
        await saveConfig()
        return true
      } catch (error) {
        console.log('Failed to save changes', error)
        return false
      }
    },
    cancelCallback: async () => {
      if (_originalConfig) {
        setFeedbackEnabled(_originalConfig.feedbackEnabled)
        setAllowFileUpload(_originalConfig.allowFileUpload)
        setSoundEnabled(_originalConfig.soundEnabled)
        setStorageLocation(_originalConfig.storageLocation)
        setAdminSecret(_originalConfig.adminSecret)
        setAllowedOrigins(_originalConfig.allowedOrigins)
      }
      return false
    },
  })

  return (
    <Section
      name="Features"
      actionButton={
        <AsyncButton
          className="hidden h-9 lg:block"
          onClick={saveConfig}
          disabled={_updating || !hasPendingChanges}
          loading={_updating}
        >
          Publish Changes
        </AsyncButton>
      }
      sticky
    >
      <Tabs defaultValue="settings">
        <div className="sticky top-36 z-50 bg-background">
          <TabsList className="z-10 mb-4 hidden grid-cols-2 lg:inline-flex">
            <TabsTrigger value="settings">Chat Settings</TabsTrigger>
            <TabsTrigger value="advanced-settings">Advanced Settings</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="settings" className="mt-8">
          <SubSection
            title="Message Feedback"
            description="Enables thumbs up/down reactions on bot messages for user feedback."
          >
            <div className="flex w-full flex-col justify-end gap-2">
              <Switch
                className="self-end"
                checked={feedbackEnabled}
                onCheckedChange={(value) => setFeedbackEnabled(value)}
              />

              <WebchatComponentPreview
                className="flex h-24 items-center justify-center rounded-lg border bg-neutral-50 px-8 "
                theme={webchatTheme}
              >
                <Message
                  direction="incoming"
                  sender={{ name: 'Bot' }}
                  id="test"
                  timestamp={new Date()}
                  block={{
                    type: 'bubble',
                    block: {
                      type: 'text',
                      text: 'This is a message',
                    },
                  }}
                >
                  <div
                    className={cn('bpMessageBlocksBubbleFeedbackContainer !absolute !-bottom-4 !left-36', {
                      '!opacity-100': feedbackEnabled,
                    })}
                  >
                    <ThumbsUp
                      className="bpMessageBlocksBubbleFeedbackIcon"
                      data-selected={feedback === 'positive'}
                      onClick={() => setFeedback('positive')}
                    />
                    <ThumbsDown
                      className="bpMessageBlocksBubbleFeedbackIcon"
                      data-selected={feedback === 'negative'}
                      onClick={() => setFeedback('negative')}
                    />
                  </div>
                </Message>
              </WebchatComponentPreview>
            </div>
          </SubSection>
          <Separator className="my-10" />
          <SubSection
            title="Allow File Upload"
            description="Allow users to upload and share files in the chat conversation."
          >
            <div className="flex w-full min-w-full max-w-full flex-col justify-end gap-2">
              <Switch
                className="self-end"
                checked={allowFileUpload}
                onCheckedChange={(value) => setAllowFileUpload(value)}
              />
              <WebchatComponentPreview
                className="pointer-events-none z-0 flex h-40 max-h-40 min-h-40 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-neutral-50 px-4 pt-3"
                theme={webchatTheme}
              >
                <div className="flex h-full w-full items-center justify-center overflow-hidden px-4">
                  <Composer
                    className="w-[95%]"
                    connected
                    allowFileUpload={allowFileUpload}
                    composerPlaceholder={composerPlaceholder}
                    storageKey="composer-display"
                  >
                    <FileCardDisplay allowFileUpload={allowFileUpload} />
                  </Composer>
                </div>
              </WebchatComponentPreview>
            </div>
          </SubSection>
          <Separator className="my-10" />
          <SubSection
            title="Message Notification Sound"
            description="Plays an alert when a new message arrives in the chat."
          >
            <div className="flex w-full flex-col justify-end gap-2">
              <Switch className="self-end" checked={soundEnabled} onCheckedChange={setSoundEnabled} />
              <div className="z-0">
                <SoundSettingPreview soundEnabled={soundEnabled ?? false} botId={botId} workspaceId={workspaceId} />
              </div>
            </div>
          </SubSection>
          <Separator className="my-10" />
          <SubSection
            title="Chat History Reset"
            description="Choose when users can continue conversations where they left off."
          >
            <Select
              value={storageLocation ? storageLocation : 'localStorage'}
              onValueChange={(value) => setStorageLocation(value as 'localStorage' | 'sessionStorage')}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select storage type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="localStorage">After tab is closed</SelectItem>
                <SelectItem value="sessionStorage">Never</SelectItem>
              </SelectContent>
            </Select>
          </SubSection>
        </TabsContent>

        <TabsContent value="advanced-settings">
          <AdvancedSettings botId={botId} workspaceId={workspaceId} />
        </TabsContent>
      </Tabs>
    </Section>
  )
}
